import { getSurveyAnswers } from "src/hooks/survey/answers";
import { updateCurrentAnswers } from "@utils/redux/slices/currentAnswers";
import { oralsHealthRiskOptions } from "src/pages/survey/currentHealthRiskMedical";
import { addSurveyAnswers } from "@utils/redux/slices/surveyAnswers";
import { getPageSetSurvey } from "./util";
import { ActionHandlerParams } from "..";

type HealthRiskOption = (typeof oralsHealthRiskOptions)[number];

export const seedCurrentHealthRiskMedicalFromDiabetesType = async ({
  store,
  pageSet,
  page,
}: ActionHandlerParams): Promise<void> => {
  const survey = getPageSetSurvey(pageSet, page);
  const surveyAnswers = getSurveyAnswers();
  const diabetesType = surveyAnswers.diabetesType?.[0];
  const currentHealthRiskValues = (surveyAnswers.currentHealthRiskMedical ||
    []) as HealthRiskOption[];

  // Map diabetesType to currentHealthRiskMedical value
  const healthRiskMapping: Partial<
    Record<typeof diabetesType, HealthRiskOption>
  > = {
    type1: "type1Diabetes",
    type2: "type2Diabetes",
  };

  // Start with existing values, but filter out any diabetes-related options
  const nonDiabetesRisks = currentHealthRiskValues.filter(
    (risk) => risk !== "type1Diabetes" && risk !== "type2Diabetes"
  );

  // If diabetesType has a value, ensure the corresponding risk is included
  const mappedRisk =
    diabetesType && healthRiskMapping[diabetesType]
      ? healthRiskMapping[diabetesType]
      : null;

  // Create a new set of health risks based on current values
  const updatedHealthRisks = mappedRisk
    ? [...nonDiabetesRisks, mappedRisk] // Add the mapped risk from diabetesType
    : nonDiabetesRisks; // If no diabetesType, use the filtered risks

  // Update the survey answers with the filtered health risks
  store.dispatch(
    addSurveyAnswers({
      currentHealthRiskMedical: updatedHealthRisks,
    })
  );
  store.dispatch(
    updateCurrentAnswers({
      namespace: survey.surveyNameSpace,
      answers: {
        currentHealthRiskMedical: updatedHealthRisks,
      },
    })
  );
};

export const seedCurrentHealthRiskMedicalFromMetabolicDiagnoses = async ({
  store,
  pageSet,
  page,
}: ActionHandlerParams): Promise<void> => {
  const survey = getPageSetSurvey(pageSet, page);
  const surveyAnswers = getSurveyAnswers();
  const metabolicDiagnoses = surveyAnswers.metabolicDiagnoses?.[0];
  const currentHealthRiskValues = (surveyAnswers.currentHealthRiskMedical ||
    []) as HealthRiskOption[];

  // Map metabolicDiagnoses to currentHealthRiskMedical value
  const healthRiskMapping: Partial<
    Record<typeof metabolicDiagnoses, HealthRiskOption>
  > = {
    type1Diabetes: "type1Diabetes",
    type2Diabetes: "type2Diabetes",
  };

  // Start with existing values, but filter out any diabetes-related options
  const nonDiabetesRisks = currentHealthRiskValues.filter(
    (risk) => risk !== "type1Diabetes" && risk !== "type2Diabetes"
  );

  // If metabolicDiagnoses has a value, ensure the corresponding risk is included
  const mappedRisk =
    metabolicDiagnoses && healthRiskMapping[metabolicDiagnoses]
      ? healthRiskMapping[metabolicDiagnoses]
      : null;

  // Create a new set of health risks based on current values
  const updatedHealthRisks = mappedRisk
    ? [...nonDiabetesRisks, mappedRisk] // Add the mapped risk from metabolicDiagnoses
    : nonDiabetesRisks; // If no metabolicDiagnoses, use the filtered risks

  // Update the survey answers with the filtered health risks
  store.dispatch(
    addSurveyAnswers({
      currentHealthRiskMedical: updatedHealthRisks,
    })
  );
  store.dispatch(
    updateCurrentAnswers({
      namespace: survey.surveyNameSpace,
      answers: {
        currentHealthRiskMedical: updatedHealthRisks,
      },
    })
  );
};
